import * as React from 'react';
import { ButtonLink } from 'components/Button';
import { COACH_MARKETPLACE_PAGE, SIGNUP_PAGE } from 'constants/pages';
import { SkillLevelValuesEnum } from 'types/generated/client';
import { tennisSkillLevelTierInfo } from 'utils/shared/tennisRatingConverter';

interface ScoutingReportWidgetProps {
  utrRating: number;
  skillLevel: SkillLevelValuesEnum;
  onFindCoachClick: () => void;
  onSignupClick: () => void;
}

const calculateProgress = (val: number, min: number, max: number) => {
  return Math.round(Math.max(5, Math.min(100, ((val - min) / (max - min)) * 100)));
};

const getNextMilestone = (utr: number): { label: string; description: string } | null => {
  if (utr < 4.0) return tennisSkillLevelTierInfo(SkillLevelValuesEnum.Beginner);
  if (utr < 7.0) return tennisSkillLevelTierInfo(SkillLevelValuesEnum.Intermediate);
  if (utr < 11.0) return tennisSkillLevelTierInfo(SkillLevelValuesEnum.Advanced);
  if (utr < 14.0) return tennisSkillLevelTierInfo(SkillLevelValuesEnum.Pro);
  return null;
};

export default function ScoutingReportWidget({
  utrRating,
  skillLevel,
  onFindCoachClick,
  onSignupClick,
}: ScoutingReportWidgetProps) {
  const tier = tennisSkillLevelTierInfo(skillLevel);
  const nextMilestone = getNextMilestone(utrRating);

  const coachSearchUrl = `${COACH_MARKETPLACE_PAGE}?skillLevel=${encodeURIComponent(skillLevel)}`;

  // Mathematical skill estimation across the typical UTR range.
  const metrics = [
    { label: 'Serve & Return', pct: calculateProgress(utrRating, 1.0, 11.0) },
    { label: 'Groundstroke Consistency', pct: calculateProgress(utrRating, 1.0, 12.0) },
    { label: 'Net Play & Volleys', pct: calculateProgress(utrRating, 3.0, 13.0) },
    { label: 'Pace, Power & Tactics', pct: calculateProgress(utrRating, 4.0, 14.0) },
  ];

  return (
    <div className="overflow-hidden rounded-2xl border border-color-divider-lightmode-primary bg-color-bg-lightmode-primary shadow-sm dark:border-color-divider-darkmode-primary dark:bg-[#1A1A1A]">
      <div className="border-b border-color-divider-lightmode-primary bg-color-bg-lightmode-secondary/30 px-6 py-4 dark:border-color-divider-darkmode-primary dark:bg-black/20">
        <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <span className="flex h-8 items-center justify-center rounded bg-color-bg-lightmode-invert px-3 text-xs font-bold uppercase tracking-widest text-color-text-lightmode-invert dark:bg-color-bg-darkmode-invert dark:text-color-text-darkmode-invert">
              Scouting Report
            </span>
            <span className="typography-product-heading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              {tier.label}
            </span>
          </div>
          <span className="font-mono text-sm font-semibold text-color-bg-lightmode-brand">
            EST. UTR {utrRating.toFixed(2)}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr]">
        <div className="border-b border-color-divider-lightmode-primary p-6 dark:border-color-divider-darkmode-primary md:border-b-0 md:border-r">
          <h3 className="typography-product-subheading mb-6 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            Estimated Skill Matrix
          </h3>
          <div className="flex flex-col gap-5">
            {metrics.map((m) => (
              <div key={m.label} className="flex flex-col gap-2">
                <div className="flex items-end justify-between">
                  <span className="typography-product-body text-sm font-medium text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    {m.label}
                  </span>
                  <span className="font-mono text-xs text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                    {m.pct}%
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-color-bg-lightmode-secondary dark:bg-color-bg-darkmode-secondary">
                  <div
                    className="h-full rounded-full bg-color-bg-lightmode-brand transition-all duration-1000 ease-out"
                    style={{ width: `${m.pct}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col p-6">
          <h3 className="typography-product-subheading mb-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            Current Profile
          </h3>
          <p className="typography-product-body mb-8 text-sm text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            {tier.description}
          </p>

          {nextMilestone && (
            <>
              <h3 className="typography-product-subheading mb-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                Path to {nextMilestone.label}
              </h3>
              <p className="typography-product-body text-sm text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                {nextMilestone.description}
              </p>
            </>
          )}

          <div className="mt-auto pt-8">
            <div className="flex flex-col gap-3">
              <ButtonLink
                href={coachSearchUrl}
                variant="brand"
                size="md"
                onClick={onFindCoachClick}
                className="w-full justify-center"
              >
                Find a tennis coach
              </ButtonLink>
              <ButtonLink
                href={SIGNUP_PAGE}
                variant="secondary"
                size="md"
                onClick={onSignupClick}
                className="w-full justify-center"
              >
                Save my rating
              </ButtonLink>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
